
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseIcon,
  Heading,
  Row,
  InitialAvatarIcon
} from '@warrenbrasil/nebraska-web';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import {
  trackIncomeHistoryLink,
  trackHelpLink,
  trackExchangeLink,
  trackPensionLink,
  trackInsuranceLink
} from './track-events';

enum ModuleType {
  EXTERNAL,
  INTERNAL,
  LANDING_PAGE
}

interface Module {
  type: ModuleType;
  icon: string;
  name: string;
  path: string;
  feature?: FeatureFlags;
  id: string;
  trackFunction?: () => void;
  shouldRender?: boolean;
}

const customerModule = namespace('CustomerModule');

@Component({
  components: {
    BaseIcon,
    Heading,
    Row,
    InitialAvatarIcon
  }
})
export default class HomeSecondaryLinks extends Vue {
  readonly NebraskaColors = NebraskaColors;

  @customerModule.State('isImpersonated')
  readonly isImpersonated!: boolean;

  @customerModule.Getter('isCustomerPJ')
  readonly isCustomerPJ!: boolean;

  private get modules(): Module[] {
    return [
      {
        type: ModuleType.INTERNAL,
        icon: 'BB0050',
        name: 'Performance',
        path: 'investments/history',
        id: 'history',
        trackFunction: trackIncomeHistoryLink
      },
      {
        type: ModuleType.INTERNAL,
        icon: 'BB0020',
        name: 'Alocação',
        path: 'products/allocation',
        id: 'alocation'
      },
      {
        type: ModuleType.INTERNAL,
        icon: 'BB0040',
        name: 'Calculadora automática de IR',
        path: '/user/income-tax-calculator',
        id: 'income-tax-calculator',
        feature: FeatureFlags.ENABLE_INCOME_TAX_CALCULATOR,
        shouldRender: !this.isCustomerPJ && !this.isImpersonated
      },
      {
        type: ModuleType.EXTERNAL,
        icon: 'BA0040',
        name: 'Câmbio',
        path: '/app/exchange',
        feature: FeatureFlags.EXCHANGE,
        id: 'exchange',
        trackFunction: trackExchangeLink
      },
      {
        type: ModuleType.LANDING_PAGE,
        icon: 'DA0040',
        name: 'Previdência',
        path: 'https://lp.warren.com.br/previdencia',
        feature: FeatureFlags.SHOW_PRIVATE_PENSION_SECTION,
        id: 'pension',
        trackFunction: trackPensionLink
      },
      {
        type: ModuleType.LANDING_PAGE,
        icon: 'BC0030',
        name: 'Seguros',
        path: 'https://lp.warren.com.br/warren-seguros',
        feature: FeatureFlags.SHOW_INSURANCE_SECTION,
        id: 'insurance',
        trackFunction: trackInsuranceLink
      },
      {
        type: ModuleType.INTERNAL,
        icon: 'EF0051',
        name: 'Me ajuda',
        path: 'help',
        id: 'help',
        trackFunction: trackHelpLink
      }
    ];
  }

  public get getModules() {
    return this.modules.filter(module => {
      let isFeatureEnabled = true;
      let shouldRender = true;

      if (module.feature)
        isFeatureEnabled = FeatureFlagsModule.isEnabled(module.feature);
      if (typeof module.shouldRender === 'boolean')
        shouldRender = module.shouldRender;

      return isFeatureEnabled && shouldRender && module;
    });
  }

  public openModule(module: Module) {
    if (module.trackFunction) module.trackFunction();

    if (module.type === ModuleType.INTERNAL) {
      this.$router.push(module.path);
    } else if (module.type === ModuleType.LANDING_PAGE) {
      window.open(module.path);
    } else {
      window.location.assign(module.path);
    }
  }
}
